import { create } from 'zustand';

import { axiosInstance } from '@/_lib/axios';
import { PromotionItem } from '@/_types/banner';
import { Language } from '@/_types/language';
import { License } from '@/_types/license';

interface SettingStore {
    license: License | null;
    adminAdBanner: PromotionItem[];
    adminAdText: PromotionItem[];
    adminLinkBanner: PromotionItem[];
    adminLogo: PromotionItem[];
    adminText: PromotionItem[];
    customerAdBanner: PromotionItem[];
    customerAdText: PromotionItem[];
    customerLinkBanner: PromotionItem[];
    customerLogo: PromotionItem[];
    customerText: PromotionItem[];
    fetchLicense: (ruid: string) => Promise<void>;
    fetchPromotions: (ruid: string) => Promise<void>;
    adBannerGetter: () => PromotionItem[];
    adTextGetter: () => PromotionItem[];
    linkBannerGetter: () => PromotionItem[];
    logoGetter: () => PromotionItem;
    textGetter: () => PromotionItem;
    languageGetter: () => Language[];
    setLicense: (license: License) => void;
    setPromotions: (data: {
        adminPromotions: PromotionItem[];
        customerPromotions: PromotionItem[];
    }) => void;
}

// 프로모션 데이터를 특정 type별로 필터링하는 도우미 함수
const filterByType = (promotions: PromotionItem[], type: string) => {
    return promotions.filter((promotion) => promotion.type === type);
};

export const useSettingStore = create<SettingStore>((set, get) => ({
    license: null,
    adminAdBanner: [],
    adminAdText: [],
    adminLinkBanner: [],
    adminLogo: [],
    adminText: [],
    customerAdBanner: [],
    customerAdText: [],
    customerLinkBanner: [],
    customerLogo: [],
    customerText: [],
    fetchLicense: async (ruid: string) => {
        try {
            // 라이센스 데이터를 API 호출 등을 통해 불러오기
            const { data } = await axiosInstance.get(`/chat/license/${ruid}`);
            set({ license: data });
        } catch (error) {
            console.error('라이센스 불러오기 실패:', error);
        }
    },
    fetchPromotions: async (ruid: string) => {
        try {
            // 프로모션 데이터를 API 호출 등을 통해 불러오기
            const { data } = await axiosInstance.get(`/chat/promotions/${ruid}`);

            const adminPromotions: PromotionItem[] = data.adminPromotions;
            const customerPromotions: PromotionItem[] = data.customerPromotions;

            set({
                adminAdBanner: filterByType(adminPromotions, 'ad_banner'),
                adminAdText: filterByType(adminPromotions, 'ad_text'),
                adminLinkBanner: filterByType(adminPromotions, 'link_banner'),
                adminLogo: filterByType(adminPromotions, 'logo'),
                adminText: filterByType(adminPromotions, 'text'),
                customerAdBanner: filterByType(customerPromotions, 'ad_banner'),
                customerAdText: filterByType(customerPromotions, 'ad_text'),
                customerLinkBanner: filterByType(customerPromotions, 'link_banner'),
                customerLogo: filterByType(customerPromotions, 'logo'),
                customerText: filterByType(customerPromotions, 'text'),
            });
        } catch (error) {
            console.error('프로모션 불러오기 실패:', error);
        }
    },
    adBannerGetter: () =>
        get().customerAdBanner.length > 0 ? get().customerAdBanner : get().adminAdBanner,
    adTextGetter: () =>
        get().customerAdText.length > 0 ? get().customerAdText : get().adminAdText,
    linkBannerGetter: () =>
        get().customerLinkBanner.length > 0 ? get().customerLinkBanner : get().adminLinkBanner,
    logoGetter: () => (get().customerLogo.length > 0 ? get().customerLogo[0] : get().adminLogo[0]),
    textGetter: () => (get().customerText.length > 0 ? get().customerText[0] : get().adminText[0]),
    languageGetter: () =>
        get().license?.customer.customerLanguages.map((customerLanguage) => ({
            id: customerLanguage.id,
            langCode: customerLanguage.language.langCode,
            name: customerLanguage.language.name,
            nativeName: customerLanguage.language.nativeName,
            img: customerLanguage.language.img,
        })) || [],

    setLicense: (license: License) => {
        set({ license });
    },

    setPromotions: (data: {
        adminPromotions: PromotionItem[];
        customerPromotions: PromotionItem[];
    }) => {
        const { adminPromotions, customerPromotions } = data;
        set({
            adminAdBanner: filterByType(adminPromotions, 'ad_banner'),
            adminAdText: filterByType(adminPromotions, 'ad_text'),
            adminLinkBanner: filterByType(adminPromotions, 'link_banner'),
            adminLogo: filterByType(adminPromotions, 'logo'),
            adminText: filterByType(adminPromotions, 'text'),
            customerAdBanner: filterByType(customerPromotions, 'ad_banner'),
            customerAdText: filterByType(customerPromotions, 'ad_text'),
            customerLinkBanner: filterByType(customerPromotions, 'link_banner'),
            customerLogo: filterByType(customerPromotions, 'logo'),
            customerText: filterByType(customerPromotions, 'text'),
        });

        const state = get();

        const allImageUrls = [
            state.logoGetter()?.imgUrl,
            ...(state.adBannerGetter()?.map((banner) => banner.imgUrl) || []),
            ...(state.linkBannerGetter()?.map((banner) => banner.imgUrl) || []),
        ].filter(Boolean) as string[];

        if (typeof window !== 'undefined') {
            allImageUrls.forEach((imageUrl) => {
                const img = document.createElement('img');
                img.src = imageUrl;
            });
        }
    },
}));
